// Risk meter data types and presets

// Re-export the generated DTOs for convenience
export type { RiskMeterDto as RiskMeterData } from '../../../../dtos/RiskMeterDto';
export type { RiskMeterSliceDto as RiskMeterSlice } from '../../../../dtos/RiskMeterSliceDto';

import type { RiskMeterDto } from '../../../../dtos/RiskMeterDto';
import type { RiskMeterVariant } from './riskMeterVariants';

export interface RiskMeterDisplayProps {
    // Which set of rendering constants to draw with. height and width override its size; every
    // other constant comes from the variant.
    variant?: RiskMeterVariant;
    height?: number;
    width?: number;

    // Display options
    showLabels?: boolean;
    showConnectors?: boolean;
    showTooltip?: boolean;
    showCenterCircle?: boolean;
    showBackground?: boolean;
    showTable?: boolean;
    showLegend?: boolean;
}

export interface RiskMeterProps extends RiskMeterDisplayProps {
    // Data source - one of these required
    grantId?: string;
    data?: RiskMeterDto;
    // Increment to trigger a refetch
    refreshKey?: number;
    // Enables Export PNG button when provided
    screenshotTitle?: string;
    screenshotFilename?: string;
}

// Convenience presets
export const RiskMeterPresets = {
    icon: {
        variant: 'icon' as const,
        showLabels: false,
        showConnectors: false,
        showTooltip: false,
        showCenterCircle: true,
        showBackground: false,
        showTable: false,
        showLegend: false
    },
    full: {
        variant: 'full' as const,
        showLabels: true,
        showConnectors: true,
        showTooltip: true,
        showCenterCircle: true,
        showBackground: true,
        showTable: true,
        showLegend: true
    }
} as const;

// Score explanation data (static, client-side only)
export interface ScoreExplanation {
    score: number;
    meaning: string;
}

// Kept terse so three explanation tables fit side by side in the legend
export const ScoreExplanations: ScoreExplanation[] = [
    { score: -2, meaning: 'More than 2 weeks early' },
    { score: -1, meaning: '1 to 2 weeks early' },
    { score: 0, meaning: 'On schedule (within a week)' },
    { score: 1, meaning: '1 to 2 weeks late' },
    { score: 2, meaning: '2 to 4 weeks late' },
    { score: 3, meaning: 'More than 4 weeks late' }
];

// Financial score bands, where the ratio is available funds over the required budget
export interface FinancialScoreExplanation {
    score: number;
    ratio: string;
    meaning: string;
}

export const FinancialScoreExplanations: FinancialScoreExplanation[] = [
    { score: -2, ratio: '1.25 or more', meaning: 'Safe / low risk' },
    { score: -1, ratio: '1.00 to 1.25', meaning: 'Moderate risk' },
    { score: 0, ratio: '0.90 to 1.00', meaning: 'Medium risk' },
    { score: 1, ratio: '0.80 to 0.90', meaning: 'High risk' },
    { score: 2, ratio: 'Under 0.80', meaning: 'Very high risk' }
];

// Buffer/Overall score explanation data
export interface BufferOverallExplanation {
    score: number;
    buffer: string;
    overall: string;
}

export const BufferOverallExplanations: BufferOverallExplanation[] = [
    { score: -2, buffer: '6 months or more', overall: 'Low risk' },
    { score: -1, buffer: '4 to 6 months', overall: 'Moderate risk' },
    { score: 0, buffer: '2 to 4 months', overall: 'Medium risk' },
    { score: 1, buffer: '1 to 2 months', overall: 'High risk' },
    { score: 2, buffer: 'Less than 1 month', overall: 'Very high risk' }
];
